import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { GzFormFieldToggleableWebComponent, GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { GzInputNumberWebComponent } from '../../../ui/gzInput';
import { GzToggleWebComponent } from '../../../ui/gzToggle';
import { GzDropdownWebComponent } from '../../../ui/gzDropdown';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import { MinValidator } from '../../../ui/gzForm/validators/min.validator';
import { GzHardCodedFormField } from '../../../ui/gzForm/formField/component/GzHardCodedFormField';
import { CompanyLicenseInformation } from '../../types/CompanyLicenseInformation';
import { LicenseMainFormViewData } from '../../licenseMainForm/types/LicenseMainFormViewData';
import _, { upperCase } from 'lodash';
import { SelectOption } from '../../../ui/gzDropdown/types';
import { WhitelistValidator } from '../../../ui/gzForm/validators/whitelist.validator';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { LicenseConstants } from '../../LicenseConstants';
import { MaxValidator } from '../../../ui/gzForm/validators/max.validator';
import { MonthlyProductType } from '../../types/MonthlyProductType';
import { GzTextWebComponent } from '../../../ui/gzText';
import { getLocalizedAsset } from '../../../utils/i18n.utils';
import { CompanyData, CompanyFeatures } from '../../../company/types/company-data';
import { GzAccordionWebComponent } from '../../../ui/gzAccordion';
import { LicenseMonthlyAddonsFormWebComponent } from '../../common/addonsForm';
import { DeepPartial } from 'typeorm';
import { FormGroupValue } from '../../../ui/gzForm/formGroup/types/form-group-value';
import { ProductTypeInformation } from '../../types/ProductTypeInformation';
import { getAvailableProductTypes } from '../../common/licenseHelper';
import { htmlEncode } from '../../../utils/escape.utils';
import { LicenseProductIdEnum } from '@nirvana/shared/license-helper';

export class LicenseMonthlySubscriptionTrialSubFormView extends BaseWebComponentView {
  public formGroup: FormGroup;
  public agreeRemoveReservedSlotsForChildren = false;

  public dom: {
    $wrapper: HTMLElement;

    $subscriptionDetailsAccordion: GzAccordionWebComponent;
    $subscriptionPreferencesLabel: GzTextWebComponent;

    $reserveSeatsFormField: GzFormFieldToggleableWebComponent<GzInputNumberWebComponent, number>;
    $availableSeatsText: GzTextWebComponent;

    $ownUseAccordion: GzAccordionWebComponent;

    ownUseProductInformation: {
      endpointSecurityInformation: ProductTypeInformation;
      bitdefenderEdrInformation: ProductTypeInformation;
    };

    $ownUseProductTypeFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;

    $ownUseAddonsSection: HTMLElement;
    $ownUseAddonsInfo: ProductTypeInformation;
    $ownUseAddonsLabel: HTMLElement;
    $ownUseMonthlyAddonsForm: LicenseMonthlyAddonsFormWebComponent;

    $resellingAccordion: GzAccordionWebComponent;

    $resellingProductsSection: HTMLElement;

    $resellingProductTypeInfo: HTMLElement;
    $resellingProductTypeLabel: HTMLElement;
    $resellingProductEndpointSecurityToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
    $resellingProductBitdefenderEdrToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;

    $resellingAddonsSection: HTMLElement;
    $resellingAddonsInfo: ProductTypeInformation;
    $resellingAddonsLabel: HTMLElement;
    $resellingMonthlyAddonsForm: LicenseMonthlyAddonsFormWebComponent;
  };

  public companionUsableToggle = new GzHardCodedFormField<boolean>(false);

  public viewData?: LicenseMainFormViewData;

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('#wrapper'),

      $subscriptionDetailsAccordion: this.shadow.querySelector('#subscription-details-section'),
      $subscriptionPreferencesLabel: this.shadow.querySelector('#subscription-preferences-label'),

      $reserveSeatsFormField: this.shadow.querySelector('#reserve-seats-form-field'),
      $availableSeatsText: this.shadow.querySelector('#available-seats-text'),

      $ownUseAccordion: this.shadow.querySelector('#own-use-section'),

      ownUseProductInformation: {
        endpointSecurityInformation: {
          $infoText: this.shadow.querySelector('#own-use-product-details-endpoint-security-text'),
          $link: this.shadow.querySelector('#own-use-product-details-endpoint-security-link'),
        },
        bitdefenderEdrInformation: {
          $infoText: this.shadow.querySelector('#own-use-product-details-bitdefender-edr-text'),
          $link: this.shadow.querySelector('#own-use-product-details-bitdefender-edr-link'),
        },
      },

      $ownUseProductTypeFormField: this.shadow.querySelector('#own-use-product-type-form-field'),

      $ownUseAddonsSection: this.shadow.querySelector('#own-use-addons-section'),
      $ownUseAddonsInfo: {
        $infoText: this.shadow.querySelector('#own-use-addons-details-text'),
        $link: this.shadow.querySelector('#own-use-addons-details-link'),
      },
      $ownUseAddonsLabel: this.shadow.querySelector('#own-use-addons-label'),
      $ownUseMonthlyAddonsForm: this.shadow.querySelector('#own-use-addons-form'),

      $resellingAccordion: this.shadow.querySelector('#reselling-section'),

      $resellingProductsSection: this.shadow.querySelector('#reselling-product-section'),
      $resellingProductTypeInfo: this.shadow.querySelector('#reselling-product-type-details'),
      $resellingProductTypeLabel: this.shadow.querySelector('#reselling-product-type-label'),
      $resellingProductEndpointSecurityToggle: this.shadow.querySelector('#reselling-product-es-toggle-form-field'),
      $resellingProductBitdefenderEdrToggle: this.shadow.querySelector('#reselling-product-edr-toggle-form-field'),

      $resellingAddonsSection: this.shadow.querySelector('#reselling-addons-section'),
      $resellingAddonsInfo: {
        $infoText: this.shadow.querySelector('#reselling-addons-details-text'),
        $link: this.shadow.querySelector('#reselling-addons-details-link'),
      },
      $resellingAddonsLabel: this.shadow.querySelector('#reselling-addons-label'),
      $resellingMonthlyAddonsForm: this.shadow.querySelector('#reselling-addons-form'),
    };

    this.setTranslatedStrings();
  }

  setTranslatedStrings() {
    const learnMoreTranslation = `<span>${htmlEncode(this.dependencies.translateFn('Learn more'))}</span>`;
    const modulesInfoText = this.dependencies.translateFn(
      'Each add-on has a fee based on usage. Enabling the add-on will add the fee to your monthly subscription, when it starts. During the trial you can test the add-ons at no cost. You can view the usage details in the Monthly License Usage report.'
    );
    const learnMoreUsage = `<span>${htmlEncode(
      this.dependencies.translateFn('Learn how to calculate the monthly usage')
    )}</span>`;

    this.dom.$subscriptionDetailsAccordion.accordionTitle = upperCase(this.dependencies.translateFn('Subscription details'));
    this.dom.$subscriptionPreferencesLabel.innerText = this.dependencies.translateFn('Subscription preferences');
    this.dom.$reserveSeatsFormField.label = this.dependencies.translateFn('Reserve seats');

    this.dom.$ownUseAccordion.accordionTitle = upperCase(this.dependencies.translateFn('Products for own use'));

    this.dom.ownUseProductInformation.endpointSecurityInformation.$infoText.innerText = this.dependencies.translateFn(
      'With {endpointSecurityProductType}, the company can access all features and add-ons available for {cloudSecurityMspProductName}.',
      {
        endpointSecurityProductType: this.stringsTranslator.getMonthlyProductType(MonthlyProductType.EndpointSecurity),
        cloudSecurityMspProductName: this.stringsTranslator.translateProductId(LicenseProductIdEnum.ProductIdMsp),
      }
    );
    this.dom.ownUseProductInformation.endpointSecurityInformation.$link.innerHTML = learnMoreTranslation;
    this.dom.ownUseProductInformation.endpointSecurityInformation.$link.href = getLocalizedAsset<string>(
      LicenseConstants.InfoLinks.CloudSecurityMsp,
      this.dependencies.getUserLanguage()
    );
    this.dom.ownUseProductInformation.bitdefenderEdrInformation.$infoText.innerText = this.dependencies.translateFn(
      '{bitdefenderEdrProductType} monitors the company`s network to early uncover suspicious activity, and provides the tools to fight-off cyber-attacks. It is suited to coexist with an endpoint security solution from a different vendor.',
      {
        bitdefenderEdrProductType: this.stringsTranslator.getMonthlyProductType(MonthlyProductType.BitdefenderEdr),
      }
    );
    this.dom.ownUseProductInformation.bitdefenderEdrInformation.$link.innerHTML = learnMoreTranslation;
    this.dom.ownUseProductInformation.bitdefenderEdrInformation.$link.href = LicenseConstants.InfoLinks.BitdefenderEdr;

    this.dom.$ownUseProductTypeFormField.label = this.dependencies.translateFn('Select product type');

    this.dom.$ownUseAddonsInfo.$infoText.textContent = modulesInfoText;
    this.dom.$ownUseAddonsInfo.$link.innerHTML = learnMoreUsage;
    this.dom.$ownUseAddonsInfo.$link.href = LicenseConstants.InfoLinks.HowToCalculateMonthlyUsage;
    this.dom.$ownUseAddonsLabel.innerText = this.dependencies.translateFn('Add-ons');

    this.dom.$resellingAccordion.accordionTitle = upperCase(this.dependencies.translateFn('Products for reselling'));

    this.dom.$resellingProductTypeInfo.innerText = this.dependencies.translateFn(
      'The partner company can assign to its client companies the following Bitdefender product types.'
    );
    this.dom.$resellingProductTypeLabel.innerText = this.dependencies.translateFn('Products to resell');
    this.dom.$resellingProductBitdefenderEdrToggle.Input.textContent = 'Bitdefender EDR';
    this.dom.$resellingProductEndpointSecurityToggle.Input.textContent = 'Endpoint security';

    this.dom.$resellingAddonsInfo.$infoText.textContent = modulesInfoText;
    this.dom.$resellingAddonsInfo.$link.innerHTML = learnMoreUsage;
    this.dom.$resellingAddonsInfo.$link.href = LicenseConstants.InfoLinks.HowToCalculateMonthlyUsage;
    this.dom.$resellingAddonsLabel.innerText = this.dependencies.translateFn('Add-ons to resell');
  }

  public getFormValue(): FormGroupValue {
    const value = _.clone(this.formGroup.value);
    const features: CompanyFeatures = _.get(value, ['specifics', 'features'], {});

    _.set(
      value,
      ['specifics', 'features'],
      _.merge(features, this.dom.$ownUseMonthlyAddonsForm.value, this.dom.$resellingMonthlyAddonsForm.value)
    );

    // adapt to format required by CompanyData
    _.set(value, ['specifics', 'licenses'], [_.get(value, ['specifics', 'license'])]);
    _.unset(value, ['specifics', 'license']);

    // set agreeRemoveReservedSlotsForChildren flag depending by the user response to warning dialog
    _.set(value, ['specifics', 'agreeRemoveReservedSlotsForChildren'], this.agreeRemoveReservedSlotsForChildren);

    return value;
  }

  public setViewData(viewData: LicenseMainFormViewData) {
    // view data already set, skip
    if (this.viewData) {
      this.updateViewData(viewData);
      return;
    }

    this.setDefaultValues();
    this.createFormGroup();
    this.updateViewData(viewData);
  }

  public setCompanyData(company: CompanyData) {
    this.dom.$wrapper.classList.add('update-context');
    const clonedCompany = _.cloneDeep(company);

    this.setSubscriptionDetails(clonedCompany);
    this.setCompanyProducts(clonedCompany.specifics.features);
  }

  public setSubscriptionDetails(company: DeepPartial<CompanyData>) {
    const license = CompanyData.getMonthlyLicense(company);

    if (!license) {
      throw new Error('setSubscriptionDetails: company does not have license');
    }

    // extract only relevant fields from company
    const subscriptionDetails = {
      specifics: {
        hasReservedSlots: !!company.specifics?.hasReservedSlots,
        license: {
          total: license.total,
        },
      },
    };

    // load subscription details into form
    this.formGroup.value = subscriptionDetails;

    if (!subscriptionDetails.specifics?.hasReservedSlots) {
      // if there are no reserved seats, the license.total field is not set so the 'value' setter does not overwrite the form value
      this.dom.$reserveSeatsFormField.setValueWithoutToggleUpdate(
        LicenseConstants.MonthlySubscription.ReserveSeats.Default
      );
      this.dom.$reserveSeatsFormField.setToggleFormValue(false);
    }
  }

  private updateViewData(viewData: LicenseMainFormViewData) {
    this.viewData = viewData;

    this.setProductTypeDropdown(viewData);
    this.setAdditionalDelegableProducts(viewData);

    this.setUpReserveSeatsFormField();
  }

  private setCompanyProducts(companyFeatures: CompanyFeatures) {
    this.formGroup.value = {
      specifics: {
        features: companyFeatures,
      },
    };

    this.dom.$ownUseProductTypeFormField.value = MonthlyProductType.getAssignedProduct(companyFeatures);

    this.dom.$ownUseMonthlyAddonsForm.value = companyFeatures;
    this.dom.$resellingMonthlyAddonsForm.value = companyFeatures;

    this.unsetUnavailableDelegableProducts();
  }

  buildSuccessView(data: unknown) {
    this.dom.$ownUseMonthlyAddonsForm.injectDependencies(this.dependencies);
    this.dom.$ownUseMonthlyAddonsForm.start().catch(console.error);

    this.dom.$resellingMonthlyAddonsForm.injectDependencies(this.dependencies);
    this.dom.$resellingMonthlyAddonsForm.start().catch(console.error);
  }

  buildErrorView(error: Error) {}

  public updateAvailableSeatsText() {
    if (!this.dom.$reserveSeatsFormField.isOn) {
      toggleVisibility(this.dom.$availableSeatsText, false);
      return;
    }

    const maxSeats = this.getMaxSlotsToReserve();
    const reservedSeats = this.dom.$reserveSeatsFormField.value as number;

    const availableSeats = Math.max(0, maxSeats - reservedSeats);

    toggleVisibility(this.dom.$availableSeatsText, reservedSeats !== 0);

    this.dom.$availableSeatsText.textContent = this.dependencies.translateFn('({availableSeats} still available)', {
      availableSeats,
    });
  }

  private setDefaultValues() {
    // set default values
    this.dom.$reserveSeatsFormField.value = LicenseConstants.MonthlySubscription.ReserveSeats.Default;
    this.dom.$reserveSeatsFormField.setToggleFormValue(false);
  }

  private createFormGroup(): void {
    this.formGroup = FormGroup.build({
      specifics: {
        hasReservedSlots: new GzHardCodedFormField<boolean>(false),

        license: {
          total: this.dom.$reserveSeatsFormField,
        },

        inheritedLicense: new GzHardCodedFormField<boolean>(true),

        features: {
          defaultProductDelegate: this.dom.$resellingProductEndpointSecurityToggle,
          companionProductDelegate: this.dom.$resellingProductBitdefenderEdrToggle,
          companionUsable: this.companionUsableToggle,
        },
      },
    });
  }

  private setProductTypeDropdown(viewData: LicenseMainFormViewData): void {
    const availableProducts = getAvailableProductTypes(viewData.context, viewData.companyLicenseInformation);
    const dropdownOptions: Array<SelectOption> = [];

    for (const option of availableProducts) {
      dropdownOptions.push({
        value: option,
        title: this.stringsTranslator.getMonthlyProductType(option),
      });
    }

    this.dom.$ownUseProductTypeFormField.Input.options = dropdownOptions;

    const assignedProductType = CompanyLicenseInformation.getEditedCompanyAssignedProductType(
      viewData.companyLicenseInformation
    );

    if (availableProducts.includes(assignedProductType)) {
      this.dom.$ownUseProductTypeFormField.Input.setFormValue(assignedProductType);
    } else if (availableProducts.length > 0) {
      // Fallback for create company case, assign at least first available product for field value
      this.dom.$ownUseProductTypeFormField.Input.setFormValue(availableProducts[0]);
    }

    this.dom.$ownUseProductTypeFormField.Validators = [
      RequiredValidator(this.dependencies.translateFn('Select a product')),
      WhitelistValidator<MonthlyProductType>(
        CompanyLicenseInformation.getParentDelegableProducts(viewData.companyLicenseInformation),
        this.dependencies.translateFn('Product type is not available')
      ),
    ];

    this.dom.$ownUseProductTypeFormField.validate(true);
  }

  private setAdditionalDelegableProducts(viewData: LicenseMainFormViewData): void {
    const monthlyProductsAvailability = MonthlyProductType.getProductsAvailability(viewData.companyLicenseInformation);

    // show/hide products based on what products the parent can delegate and what product type the child has assigned
    const canDelegateEndpointSecurity = monthlyProductsAvailability[MonthlyProductType.EndpointSecurity];
    const canDelegateBitdefenderEdr = monthlyProductsAvailability[MonthlyProductType.BitdefenderEdr];

    toggleVisibility(this.dom.$resellingProductEndpointSecurityToggle, canDelegateEndpointSecurity);
    toggleVisibility(this.dom.$resellingProductBitdefenderEdrToggle, canDelegateBitdefenderEdr);
  }

  private unsetUnavailableDelegableProducts() {
    if (!this.viewData) {
      return;
    }

    const monthlyProductsAvailability = MonthlyProductType.getProductsAvailability(
      this.viewData.companyLicenseInformation
    );

    const canDelegateEndpointSecurity = monthlyProductsAvailability[MonthlyProductType.EndpointSecurity];
    const canDelegateBitdefenderEdr = monthlyProductsAvailability[MonthlyProductType.BitdefenderEdr];

    if (!canDelegateEndpointSecurity) {
      this.dom.$resellingProductEndpointSecurityToggle.value = false;
    }

    if (!canDelegateBitdefenderEdr) {
      this.dom.$resellingProductBitdefenderEdrToggle.value = false;
    }
  }

  private setUpReserveSeatsFormField(): void {
    const minSlotsToReserve = this.getMinSlotsToReserve();
    const maxSlotsToReserve = this.getMaxSlotsToReserve();

    if (!maxSlotsToReserve) {
      this.dom.$reserveSeatsFormField.setEnabled(false);
      return;
    }

    this.dom.$reserveSeatsFormField.setEnabled(true);

    this.dom.$reserveSeatsFormField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MinValidator(
        minSlotsToReserve,
        this.dependencies.translateFn('The minimum value for this field is {minSlotsToReserve}', {
          minSlotsToReserve,
        })
      ),
      MaxValidator(
        maxSlotsToReserve,
        this.dependencies.translateFn('The maximum value for this field is {maxValue}', { maxValue: maxSlotsToReserve })
      ),
    ];

    this.dom.$reserveSeatsFormField.Input.min = LicenseConstants.MonthlySubscription.ReserveSeats.MinInput;
  }

  private getMinSlotsToReserve() {
    if (!this.viewData?.companyLicenseInformation.reservationSupported) {
      return LicenseConstants.MonthlySubscription.ReserveSeats.MinValidator;
    }

    const minSlotsToReserve = this.viewData.companyLicenseInformation.minSlotsToReserve;
    if (typeof minSlotsToReserve !== 'number' || minSlotsToReserve <= 0) {
      return LicenseConstants.MonthlySubscription.ReserveSeats.MinValidator;
    }

    return minSlotsToReserve;
  }

  private getMaxSlotsToReserve() {
    if (!this.viewData?.companyLicenseInformation.reservationSupported) {
      return 0;
    }

    const maxSlotsToReserve = this.viewData.companyLicenseInformation.maxSlotsToReserve;
    if (typeof maxSlotsToReserve !== 'number') {
      return 0;
    }

    return maxSlotsToReserve;
  }

  public updateReservedSeatsFormFieldAvailability(reservedSeatsToggleIsOn: boolean) {
    this.dom.$reserveSeatsFormField.disabled = !reservedSeatsToggleIsOn;
    this.dom.$reserveSeatsFormField.setToggleFormValue(reservedSeatsToggleIsOn);
  }
}
